import React, { useState } from "react";
import classNames from "classnames";
import { CheckIcon, SearchIcon } from "@icons";
import { WorkspaceSchema } from "@common/schemas/workspace.schema";
import { WorkspaceAvatar } from "@design-system/Avatar/WorkspaceAvatar";
import type { AgentAccessTargetFormValues } from "./agentAccessTarget.helpers";
import styles from "./AuthorizePage.module.scss";

interface AgentAccessTargetPickerProps {
    values: AgentAccessTargetFormValues;
    workspaces: WorkspaceSchema[];
    onChange: (values: AgentAccessTargetFormValues) => void;
    errorMessage?: string | null;
}

function getSelectedWorkspaceIds(
    values: AgentAccessTargetFormValues,
    workspaces: WorkspaceSchema[],
): string[] {
    if (values.targetType === "all") {
        return workspaces.map((workspace) => workspace.id);
    }
    return values.workspaceIds;
}

function buildValuesFromSelection(
    selectedIds: string[],
    workspaces: WorkspaceSchema[],
): AgentAccessTargetFormValues {
    const isEveryWorkspaceSelected =
        workspaces.length > 0 && selectedIds.length === workspaces.length;
    return {
        targetType: isEveryWorkspaceSelected ? "all" : "workspaces",
        organizationIds: [],
        workspaceIds: isEveryWorkspaceSelected ? [] : selectedIds,
    };
}

const RowCheckbox: React.FC<{ isChecked: boolean }> = ({ isChecked }) => (
    <span
        className={classNames(styles.wsCheckbox, {
            [styles.wsCheckboxChecked]: isChecked,
        })}
    >
        {isChecked && <CheckIcon className={styles.wsCheckIcon} />}
    </span>
);

export const AgentAccessTargetPicker: React.FC<
    AgentAccessTargetPickerProps
> = ({ values, workspaces, onChange, errorMessage }) => {
    const [search, setSearch] = useState("");
    const normalizedSearch = search.trim().toLowerCase();
    const filteredWorkspaces = normalizedSearch
        ? workspaces.filter((workspace) =>
              workspace.name.toLowerCase().includes(normalizedSearch),
          )
        : workspaces;

    const selectedIds = getSelectedWorkspaceIds(values, workspaces);
    const selectedIdSet = new Set(selectedIds);
    const isAllSelected =
        workspaces.length > 0 && selectedIds.length === workspaces.length;

    const toggleWorkspace = (id: string) => {
        const nextIds = selectedIdSet.has(id)
            ? selectedIds.filter((currentId) => currentId !== id)
            : [...selectedIds, id];
        onChange(buildValuesFromSelection(nextIds, workspaces));
    };

    const toggleAll = () => {
        const nextIds = isAllSelected
            ? []
            : workspaces.map((workspace) => workspace.id);
        onChange(buildValuesFromSelection(nextIds, workspaces));
    };

    return (
        <section className={styles.section}>
            <h4 className={styles.sectionHeading}>Workspace access</h4>
            <div className={styles.wsCard}>
                <div className={styles.wsSearch}>
                    <SearchIcon className={styles.wsSearchIcon} />
                    <input
                        type="text"
                        className={styles.wsSearchInput}
                        placeholder="Search..."
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </div>
                <div className={styles.wsList}>
                    <button
                        type="button"
                        className={classNames(styles.wsRow, styles.wsRowAll)}
                        onClick={toggleAll}
                    >
                        <span className={styles.wsRowName}>All workspaces</span>
                        <RowCheckbox isChecked={isAllSelected} />
                    </button>
                    {filteredWorkspaces.map((workspace) => (
                        <button
                            type="button"
                            key={workspace.id}
                            className={styles.wsRow}
                            onClick={() => toggleWorkspace(workspace.id)}
                        >
                            <span className={styles.wsRowLabel}>
                                <WorkspaceAvatar
                                    workspace={{
                                        name: workspace.name,
                                        iconColor: workspace.iconColor,
                                    }}
                                    size="xs"
                                    aria-hidden
                                />
                                <span className={styles.wsRowName}>
                                    {workspace.name}
                                </span>
                            </span>
                            <RowCheckbox
                                isChecked={selectedIdSet.has(workspace.id)}
                            />
                        </button>
                    ))}
                </div>
            </div>

            {errorMessage && (
                <div className={styles.fieldError}>{errorMessage}</div>
            )}
        </section>
    );
};
